import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/** Paleta de cores para o PDF (Modo Claro) */
const PDF_COLORS = {
    primaryDark: [30, 41, 59],       // slate-800
    primaryMid: [51, 65, 85],        // slate-700
    accent: [79, 70, 229],           // indigo-600
    accentLight: [224, 231, 255],    // indigo-50
    success: [5, 150, 105],          // emerald-600
    successLight: [209, 250, 229],   // emerald-100
    danger: [220, 38, 38],           // red-600
    dangerLight: [254, 226, 226],    // red-100
    warning: [217, 119, 6],          // amber-600
    warningLight: [254, 243, 199],   // amber-100
    blue: [37, 99, 235],             // blue-600
    blueLight: [219, 234, 254],      // blue-100
    white: [255, 255, 255],
    textPrimary: [15, 23, 42],       // slate-900
    textSecondary: [100, 116, 139],  // slate-500
    borderColor: [226, 232, 240],    // slate-200
    bgLight: [248, 250, 252],        // slate-50
    bgMid: [241, 245, 249],          // slate-100
};

const PAGE_W = 210; // A4 largura mm
const PAGE_H = 297; // A4 altura mm
const MARGIN = 15;
const CONTENT_W = PAGE_W - MARGIN * 2;

/** Remove caracteres especiais (emojis) não-Latin1 para o jsPDF */
function sanitizeText(text) {
    if (!text) return '';
    return String(text)
        .replace(/["“]/g, '"')
        .replace(/['’]/g, "'")
        .replace(/[—–]/g, '-')
        .replace(/…/g, '...')
        // eslint-disable-next-line no-control-regex
        .replace(/[^\x00-\xFF]/g, '')
        .trim();
}

/** Resolve IDs de setores para nomes usando a lista de setores */
function resolveSectorNames(sectorIds, setoresList) {
    if (!sectorIds || sectorIds.length === 0) return 'Sem setor cadastrado';
    if (!setoresList || setoresList.length === 0) {
        return sectorIds.join(', ');
    }
    const sectorMap = new Map(setoresList.map(s => [s.id, s.name]));
    const names = sectorIds.map(id => sectorMap.get(id) || id);
    return names.join(', ');
}

// ═══════════════════════════════════════════════════════════════════════════
//  ÍCONES VETORIAIS
// ═══════════════════════════════════════════════════════════════════════════

/** Desenha um ícone de gráfico de barras */
function drawBarChartIcon(doc, x, y, size, color) {
    const s = size / 14;
    doc.setFillColor(...color);
    // Barras
    doc.rect(x + 1 * s, y + 8 * s, 3 * s, 5 * s, 'F');
    doc.rect(x + 5.5 * s, y + 4 * s, 3 * s, 9 * s, 'F');
    doc.rect(x + 10 * s, y + 1 * s, 3 * s, 12 * s, 'F');
    // Linha base
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4 * s);
    doc.line(x, y + 13 * s, x + 14 * s, y + 13 * s);
}

/** Desenha um ícone de cérebro/IA simplificado */
function drawBrainIcon(doc, x, y, size, color) {
    const cx = x + size / 2;
    const cy = y + size / 2;
    const r = size / 2;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.3);
    // Contorno do cérebro com dois hemisférios
    doc.circle(cx - r * 0.3, cy - r * 0.15, r * 0.55, 'S');
    doc.circle(cx + r * 0.3, cy - r * 0.15, r * 0.55, 'S');
    doc.circle(cx - r * 0.15, cy + r * 0.35, r * 0.4, 'S');
    doc.circle(cx + r * 0.15, cy + r * 0.35, r * 0.4, 'S');
    // Ponto central
    doc.setFillColor(...color);
    doc.circle(cx, cy, 0.4, 'F');
}

/** Desenha um ícone de lista/clipboard */
function drawClipboardIcon(doc, x, y, size, color) {
    const s = size / 14;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    // Contorno do clipboard
    doc.roundedRect(x + 1 * s, y + 2 * s, 12 * s, 11 * s, 0.5, 0.5, 'S');
    // Clip no topo
    doc.setFillColor(...color);
    doc.roundedRect(x + 4 * s, y + 0.5 * s, 6 * s, 2.5 * s, 0.4, 0.4, 'F');
    // Linhas de texto
    doc.setLineWidth(0.4);
    doc.line(x + 3.5 * s, y + 6 * s, x + 10.5 * s, y + 6 * s);
    doc.line(x + 3.5 * s, y + 8.5 * s, x + 10.5 * s, y + 8.5 * s);
    doc.line(x + 3.5 * s, y + 11 * s, x + 8 * s, y + 11 * s);
}

// ═══════════════════════════════════════════════════════════════════════════
//  ELEMENTOS VISUAIS E COMPONENTES DO PDF
// ═══════════════════════════════════════════════════════════════════════════

/** Gera imagem base64 de um gráfico de rosca (arco proporcional) */
function generateDonutChartImage(percent, colorHex, bgColorHex) {
    const size = 120; // tamanho do canvas em px
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    const center = size / 2;
    const radius = 46; // raio
    const lineWidth = 12; // espessura da linha
    
    // Desenha o círculo de fundo cinza claro
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = bgColorHex || '#e2e8f0';
    ctx.lineWidth = lineWidth;
    ctx.stroke();
    
    // Desenha o arco proporcional ao percentual
    const startAngle = -Math.PI / 2;
    const endAngle = startAngle + (2 * Math.PI * (percent / 100));
    
    ctx.beginPath();
    ctx.arc(center, center, radius, startAngle, endAngle);
    ctx.strokeStyle = colorHex;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.stroke();
    
    return canvas.toDataURL('image/png');
}

/** Desenha cabeçalho padrão em cada página */
function drawPageHeader(doc, pageNum, attendantName) {
    // Faixa superior
    doc.setFillColor(...PDF_COLORS.primaryDark);
    doc.rect(0, 0, PAGE_W, 12, 'F');
    doc.setFontSize(7.5);
    doc.setTextColor(200, 200, 200);
    doc.setFont('helvetica', 'normal');
    doc.text('Relatorio Individual de Performance - Connect10', MARGIN, 7.5);
    doc.text(sanitizeText(attendantName), PAGE_W / 2, 7.5, { align: 'center' });
    doc.text(`Pagina ${pageNum}`, PAGE_W - MARGIN, 7.5, { align: 'right' });
}

/** Desenha rodapé padrão em cada página */
function drawPageFooter(doc, generatedAt) {
    const y = PAGE_H - 10;
    doc.setDrawColor(...PDF_COLORS.borderColor);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y - 2, PAGE_W - MARGIN, y - 2);
    doc.setFontSize(7);
    doc.setTextColor(...PDF_COLORS.textSecondary);
    doc.text('CONNECT10 INTERNET - Gestao de Atendimento & Qualidade', MARGIN, y + 2.5);
    if (generatedAt) {
        doc.text(`Gerado em: ${new Date(generatedAt).toLocaleString('pt-BR')}`, PAGE_W - MARGIN, y + 2.5, { align: 'right' });
    }
}

/** Verifica espaço disponível na página e cria nova se necessário */
function ensureSpace(doc, y, needed, pageNum, attendantName, generatedAt) {
    if (y + needed > PAGE_H - 18) {
        drawPageFooter(doc, generatedAt);
        pageNum++;
        doc.addPage();
        drawPageHeader(doc, pageNum, attendantName);
        y = 20; // margem inicial abaixo do header
    }
    return { y, pageNum };
}

/** Renderiza seção de título no PDF, opcionalmente com ícone vetorial */
function renderSectionTitle(doc, title, y, iconFn, iconColor) {
    let textX = MARGIN + 6;
    if (iconFn && iconColor) {
        iconFn(doc, MARGIN, y, 6, iconColor);
        textX = MARGIN + 8;
    } else {
        doc.setFillColor(...PDF_COLORS.accent);
        doc.rect(MARGIN, y, 4, 6, 'F');
    }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PDF_COLORS.textPrimary);
    doc.text(sanitizeText(title), textX, y + 4.8);
    return y + 9;
}

/** Converte elemento DOM do gráfico em imagem base64 e insere no PDF */
async function insertChartAsImage(doc, element, y, maxH) {
    // Clona o elemento
    const clone = element.cloneNode(true);
    
    // Remove classes do tema escuro e força o fundo claro com fontes escuras
    clone.classList.remove('dark', 'bg-slate-900/50');
    clone.classList.add('bg-white');
    clone.style.backgroundColor = '#ffffff';
    clone.style.color = '#0f172a';
    clone.style.width = element.offsetWidth + 'px';
    clone.style.height = element.offsetHeight + 'px';
    
    // Ajusta as cores dos textos internos do SVG (Recharts)
    const texts = clone.querySelectorAll('.recharts-text, text');
    texts.forEach(text => {
        text.style.fill = '#475569';
    });
    
    // Ajusta as cores das linhas de grade do Recharts
    const gridLines = clone.querySelectorAll('.recharts-cartesian-grid line');
    gridLines.forEach(line => {
        line.style.stroke = '#e2e8f0';
    });
    
    // Ajusta a cor dos textos das legendas
    const legendTexts = clone.querySelectorAll('.recharts-legend-item-text');
    legendTexts.forEach(text => {
        text.style.color = '#475569';
    });

    // Anexa temporariamente no corpo do documento de forma invisível
    clone.style.position = 'absolute';
    clone.style.top = '-9999px';
    clone.style.left = '-9999px';
    document.body.appendChild(clone);
    
    let canvas;
    try {
        canvas = await html2canvas(clone, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
        });
    } finally {
        document.body.removeChild(clone);
    }
    
    const imgData = canvas.toDataURL('image/png');
    const ratio = canvas.width / canvas.height;
    let w = CONTENT_W;
    let h = w / ratio;
    if (h > maxH) {
        h = maxH;
        w = h * ratio;
    }
    const x = MARGIN + (CONTENT_W - w) / 2;
    doc.addImage(imgData, 'PNG', x, y, w, h);
    return h;
}

/** Renderiza os Cards de KPIs de performance (Visual Original Limpo) */
function renderAttendantKpis(doc, attendant, y) {
    const displayScore = (value, digits) => Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : '-';
    const rank = Number.isFinite(Number(attendant.actualRank))
        ? Number(attendant.actualRank) + 1
        : attendant.rank;
    const cards = [
        {
            label: 'Nota TMA',
            value: displayScore(attendant.tmaScore ?? attendant.efficiencyScore, 1),
            sub: attendant.tmaDeviation ? `${attendant.tmaDeviation > 0 ? '+' : ''}${attendant.tmaDeviation.toFixed(1)}% vs Mediana` : 'Desvio medio',
            bg: PDF_COLORS.blueLight,
            accent: PDF_COLORS.blue,
        },
        {
            label: 'Nota Avaliacao',
            value: displayScore(attendant.evalScore ?? attendant.qualityScore, 1),
            sub: 'Satisfacao do cliente',
            bg: PDF_COLORS.warningLight,
            accent: PDF_COLORS.warning,
        },
        {
            label: 'Nota Volume',
            value: displayScore(attendant.volumeScore ?? attendant.productivityScore, 1),
            sub: `${attendant.attendancesCount || 0} Atendimentos`,
            bg: PDF_COLORS.successLight,
            accent: PDF_COLORS.success,
        },
        {
            label: 'Nota Final',
            value: displayScore(attendant.finalScore, 2),
            sub: rank ? `${rank}o Lugar no Ranking` : 'Posicao no ranking indisponivel',
            bg: PDF_COLORS.accentLight,
            accent: PDF_COLORS.accent,
        }
    ];

    const cardW = (CONTENT_W - 9) / 4;
    const cardH = 26;

    cards.forEach((card, i) => {
        const x = MARGIN + i * (cardW + 3);
        // Card Background
        doc.setFillColor(...card.bg);
        doc.roundedRect(x, y, cardW, cardH, 2, 2, 'F');
        
        // Barra lateral colorida de destaque
        doc.setFillColor(...card.accent);
        doc.roundedRect(x, y, 2.5, cardH, 1, 1, 'F');

        // Label
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.textSecondary);
        doc.text(sanitizeText(card.label), x + 5, y + 6);

        // Valor principal
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        doc.text(sanitizeText(card.value), x + 5, y + 15);

        // Sublabel
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textSecondary);
        doc.text(sanitizeText(card.sub), x + 5, y + 21);
    });

    return y + cardH + 6;
}

/** Calcula o layout (linhas) das tags/chips de categoria sem desenhar, para permitir medir a altura antes */
function layoutTagChips(doc, tags, maxWidth) {
    const paddingX = 2;
    const gap = 1.5;
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');

    const rows = [[]];
    let rowWidth = 0;
    tags.forEach(tag => {
        const text = sanitizeText(tag);
        const w = doc.getTextWidth(text) + paddingX * 2;
        if (rowWidth + w > maxWidth && rowWidth > 0) {
            rows.push([]);
            rowWidth = 0;
        }
        rows[rows.length - 1].push({ text, w });
        rowWidth += w + gap;
    });
    return rows;
}

/** Desenha as tags/chips de categoria previamente calculadas por layoutTagChips */
function drawTagChips(doc, rows, x, y, textColor, bgColor) {
    const paddingX = 2;
    const gap = 1.5;
    const chipH = 4.2;
    const rowGap = 1.5;

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');

    rows.forEach((row, rowIdx) => {
        let cx = x;
        const cy = y + rowIdx * (chipH + rowGap);
        row.forEach(chip => {
            doc.setFillColor(...bgColor);
            doc.roundedRect(cx, cy, chip.w, chipH, 1, 1, 'F');
            doc.setTextColor(...textColor);
            doc.text(chip.text, cx + paddingX, cy + chipH - 1.2);
            cx += chip.w + gap;
        });
    });

    return y + rows.length * chipH + (rows.length - 1) * rowGap;
}

/**
 * Calcula a quebra de linha de um texto com um trecho em negrito (ex: parte do comentário que
 * evidencia o ponto forte/fraco apontado pela IA), medindo cada palavra com a largura real da
 * fonte que será usada para desenhá-la (negrito é mais largo que itálico/normal no Helvetica).
 * Não desenha nada - apenas calcula o layout, para que a altura do card possa ser determinada
 * ANTES de decidir a quebra de página, e o desenho reaproveite exatamente o mesmo cálculo.
 */
function layoutMixedText(doc, fullText, highlight, maxWidth, fontSize, baseFontStyle) {
    doc.setFontSize(fontSize);

    let highlightStart = -1;
    let highlightEnd = -1;
    if (highlight) {
        const idx = fullText.indexOf(highlight);
        if (idx !== -1) {
            highlightStart = idx;
            highlightEnd = idx + highlight.length;
        }
    }

    const tokens = [];
    const re = /(\s+|\S+)/g;
    let match;
    let charIdx = 0;
    while ((match = re.exec(fullText)) !== null) {
        tokens.push({ text: match[0], start: charIdx, end: charIdx + match[0].length, isSpace: /^\s+$/.test(match[0]) });
        charIdx += match[0].length;
    }

    const lines = [[]];
    let lineWidth = 0;
    let lineHasContent = false;

    tokens.forEach(token => {
        const isBold = !token.isSpace && highlightStart !== -1 && token.start < highlightEnd && token.end > highlightStart;
        doc.setFont('helvetica', isBold ? 'bold' : baseFontStyle);
        const w = doc.getTextWidth(token.text);

        if (token.isSpace) {
            if (lineHasContent) {
                lines[lines.length - 1].push({ text: token.text, width: w, bold: false });
                lineWidth += w;
            }
            return;
        }

        if (lineHasContent && lineWidth + w > maxWidth) {
            lines.push([]);
            lineWidth = 0;
            lineHasContent = false;
        }

        lines[lines.length - 1].push({ text: token.text, width: w, bold: isBold });
        lineWidth += w;
        lineHasContent = true;
    });

    return lines;
}

/** Desenha um layout previamente calculado por layoutMixedText, retornando o número de linhas desenhadas */
function drawMixedTextLayout(doc, lines, x, y, lineHeight, fontSize, normalColor, boldColor, baseFontStyle) {
    doc.setFontSize(fontSize);
    let cy = y;
    lines.forEach(line => {
        let cx = x;
        line.forEach(token => {
            doc.setFont('helvetica', token.bold ? 'bold' : baseFontStyle);
            doc.setTextColor(...(token.bold ? boldColor : normalColor));
            doc.text(token.text, cx, cy);
            cx += token.width;
        });
        cy += lineHeight;
    });
    return lines.length;
}

/** Renderiza a lista de exemplos de atendimentos (bons e ruins) identificados pela IA, com quebra de página dinâmica */
function renderAttendanceExamples(doc, examples, y, pageNum, attendantName, generatedAt) {
    const innerW = CONTENT_W - 8;

    for (let i = 0; i < examples.length; i++) {
        const ex = examples[i];
        const isRuim = ex.tipo === 'ruim';
        const bgColor = isRuim ? PDF_COLORS.dangerLight : PDF_COLORS.successLight;
        const accentColor = isRuim ? PDF_COLORS.danger : PDF_COLORS.success;
        const label = isRuim ? 'MAU ATENDIMENTO' : 'BOM ATENDIMENTO';

        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        const motivoLines = doc.splitTextToSize(sanitizeText(ex.motivo || ''), innerW);

        const sanitizedComentario = ex.comentario ? sanitizeText(ex.comentario) : '';
        const sanitizedTrecho = ex.trechoDestaque ? sanitizeText(ex.trechoDestaque) : '';
        const comentarioFullText = sanitizedComentario ? `"${sanitizedComentario}"` : '';
        const comentarioLayout = comentarioFullText ? layoutMixedText(doc, comentarioFullText, sanitizedTrecho, innerW, 7, 'italic') : [];
        const tagRows = (ex.tags && ex.tags.length > 0) ? layoutTagChips(doc, ex.tags, innerW) : [];

        let cardH = 7; // cabeçalho (badge + protocolo/data/nota)
        cardH += motivoLines.length * 3.6 + 2;
        if (comentarioLayout.length > 0) cardH += comentarioLayout.length * 3.6 + 2;
        if (tagRows.length > 0) cardH += tagRows.length * 4.2 + (tagRows.length - 1) * 1.5 + 2;
        cardH += 3; // padding inferior

        const spaceCheck = ensureSpace(doc, y, cardH + 3, pageNum, attendantName, generatedAt);
        y = spaceCheck.y;
        pageNum = spaceCheck.pageNum;

        doc.setFillColor(...bgColor);
        doc.roundedRect(MARGIN, y, CONTENT_W, cardH, 2, 2, 'F');
        doc.setDrawColor(...accentColor);
        doc.setLineWidth(0.3);
        doc.roundedRect(MARGIN, y, CONTENT_W, cardH, 2, 2, 'S');

        let cy = y + 4.5;

        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...accentColor);
        doc.text(label, MARGIN + 4, cy);

        const formattedDate = ex.data ? ex.data.split('-').reverse().join('/') : null;
        let headerRight = `Protocolo: ${sanitizeText(ex.protocolo || '-')}`;
        if (formattedDate) headerRight += `   |   ${formattedDate}`;
        if (ex.nota !== null && ex.nota !== undefined) headerRight += `   |   Nota: ${Number(ex.nota).toFixed(1)}`;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...PDF_COLORS.textSecondary);
        doc.text(sanitizeText(headerRight), MARGIN + CONTENT_W - 4, cy, { align: 'right' });

        cy += 4.5;
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        doc.text(motivoLines, MARGIN + 4, cy);
        cy += motivoLines.length * 3.6 + 2;

        if (comentarioLayout.length > 0) {
            const lineCount = drawMixedTextLayout(
                doc,
                comentarioLayout,
                MARGIN + 4,
                cy,
                3.6,
                7,
                PDF_COLORS.textSecondary,
                accentColor,
                'italic'
            );
            cy += lineCount * 3.6 + 2;
        }

        if (tagRows.length > 0) {
            drawTagChips(doc, tagRows, MARGIN + 4, cy, accentColor, PDF_COLORS.white);
        }

        y += cardH + 3;
    }

    return { y, pageNum };
}

/** Renderiza a Tabela de observações com suporte a quebras de página dinâmicas e indicador colorido de nota */
function renderObservationsTable(doc, observations, y, colWidths, attendantName, generatedAt, pageNum) {
    const totalW = colWidths.reduce((a, b) => a + b, 0);
    const headerH = 7.5;

    // Desenha o cabeçalho da tabela
    const drawTableHeader = (curY) => {
        doc.setFillColor(...PDF_COLORS.primaryDark);
        doc.rect(MARGIN, curY, totalW, headerH, 'F');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.white);

        const headers = ['Protocolo', 'Data', 'Nota', 'Feedback/Comentario'];
        let xCursor = MARGIN + 2;
        headers.forEach((h, i) => {
            doc.text(h, xCursor, curY + 5);
            xCursor += colWidths[i];
        });
        return curY + headerH;
    };

    y = drawTableHeader(y);

    // Exibe as 15 últimas observações completas
    const rowsToShow = observations.slice(0, 15);

    for (let rowIdx = 0; rowIdx < rowsToShow.length; rowIdx++) {
        const obs = rowsToShow[rowIdx];
        const comm = obs.comment || '-';
        const sanitizedComm = sanitizeText(comm);
        const commentLines = doc.splitTextToSize(sanitizedComm, 114);

        // Calcula a altura da linha: mínimo 7.5, caso contrário baseia-se no número de linhas
        const lineH = Math.max(7.5, (commentLines.length * 4) + 3.5);

        // Verifica espaço na página atual. Se não houver, adiciona nova página e redesenha o cabeçalho
        const spaceCheck = ensureSpace(doc, y, lineH, pageNum, attendantName, generatedAt);
        if (spaceCheck.pageNum !== pageNum) {
            pageNum = spaceCheck.pageNum;
            y = spaceCheck.y;
            y = drawTableHeader(y);
        }

        // Zebra striping
        if (rowIdx % 2 === 0) {
            doc.setFillColor(...PDF_COLORS.bgLight);
            doc.rect(MARGIN, y, totalW, lineH, 'F');
        }

        // Borda externa da linha
        doc.setDrawColor(...PDF_COLORS.borderColor);
        doc.setLineWidth(0.2);
        doc.rect(MARGIN, y, totalW, lineH, 'S');

        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textPrimary);

        let xCursor = MARGIN + 2;
        
        // Protocolo
        doc.text(sanitizeText(obs.protocol || '-'), xCursor, y + 5);
        xCursor += colWidths[0];

        // Data
        const formattedDate = obs.date ? obs.date.split('-').reverse().join('/') : '-';
        doc.text(sanitizeText(formattedDate), xCursor, y + 5);
        xCursor += colWidths[1];

        // Nota com ponto de indicação de cor
        const scoreVal = obs.score !== null && obs.score !== undefined ? obs.score.toFixed(1) : '-';
        if (obs.score !== null && obs.score !== undefined) {
            let scoreColor = PDF_COLORS.success;
            if (obs.score < 3) scoreColor = PDF_COLORS.danger;
            else if (obs.score < 4) scoreColor = PDF_COLORS.warning;
            doc.setFillColor(...scoreColor);
            doc.circle(xCursor + 1, y + 4, 1.2, 'F');
            doc.text(sanitizeText(scoreVal), xCursor + 4, y + 5);
        } else {
            doc.text(sanitizeText(scoreVal), xCursor, y + 5);
        }
        xCursor += colWidths[2];

        // Comentários (com quebra automática e suporte a múltiplas linhas)
        doc.text(commentLines, xCursor, y + 5);

        y += lineH;
    }

    return { y: y + 4, pageNum };
}

/** Monta o relatório no layout clássico, sem detalhes de implementação. */
export async function buildAttendantPDF(attendant, observations, aiAnalysis, chartElementId = null, setoresList = [], reportContext = {}) {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const generatedAt = new Date().toISOString();
    let pageNum = 1;
    let y = MARGIN;

    // Cabeçalho da página 1
    drawPageHeader(doc, pageNum, attendant.name);
    y += 10;

    // ── CAPA DO RELATÓRIO (Visual Original Compacto) ──────────────────────────
    doc.setFillColor(...PDF_COLORS.bgMid);
    doc.roundedRect(MARGIN, y, CONTENT_W, 26, 2.5, 2.5, 'F');
    doc.setDrawColor(...PDF_COLORS.borderColor);
    doc.setLineWidth(0.4);
    doc.roundedRect(MARGIN, y, CONTENT_W, 26, 2.5, 2.5, 'S');

    // Informações Principais
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PDF_COLORS.textPrimary);
    doc.text(sanitizeText(attendant.name), MARGIN + 5, y + 8);

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...PDF_COLORS.textSecondary);
    const sectorsStr = resolveSectorNames(attendant.setores, setoresList);
    doc.text(`Setor(es): ${sanitizeText(sectorsStr)}`, MARGIN + 5, y + 14);
    doc.text(`Periodo do Relatorio: Ultimos 3 meses | Avaliacoes consolidadas no banco`, MARGIN + 5, y + 20);

    // Ranking Badge Retangular Original
    const rank = Number.isFinite(Number(attendant.actualRank))
        ? Number(attendant.actualRank) + 1
        : attendant.rank;
    const rankStr = rank ? `${rank}o Lugar` : 'Ranking';
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(...PDF_COLORS.accent);
    doc.roundedRect(PAGE_W - MARGIN - 30, y + 5, 25, 7, 1.5, 1.5, 'F');
    doc.setTextColor(...PDF_COLORS.white);
    doc.text(rankStr, PAGE_W - MARGIN - 17.5, y + 10, { align: 'center' });

    y += 32;

    // ── CARDS DE KPIS ────────────────────────────────────────────────────────
    y = renderSectionTitle(doc, 'Indicadores de Desempenho (OPA Suite)', y, drawBarChartIcon, PDF_COLORS.accent);
    y = renderAttendantKpis(doc, attendant, y);
    y += 2;

    // ── GRÁFICO DE EVOLUÇÃO SEMANAL (se fornecido o elemento DOM) ────────────
    const chartEl = chartElementId && document.getElementById(chartElementId);
    if (chartEl) {
        ({ y, pageNum } = ensureSpace(doc, y, 75, pageNum, attendant.name, generatedAt));
        y = renderSectionTitle(doc, 'Evolucao das Notas Semanais', y, drawBarChartIcon, PDF_COLORS.accent);
        try {
            const h = await insertChartAsImage(doc, chartEl, y, 55);
            y += h + 6;
        } catch (e) {
            console.error('Falha ao renderizar grafico no PDF:', e);
            doc.setFontSize(8);
            doc.setTextColor(...PDF_COLORS.textSecondary);
            doc.text('[Grafico indisponivel para renderizacao]', MARGIN + 5, y + 5);
            y += 10;
        }
    }

    // ── SEÇÃO: DIAGNÓSTICO DE IA (DEEPSEEK) ───────────────────────────────────
    if (aiAnalysis) {
        ({ y, pageNum } = ensureSpace(doc, y, 65, pageNum, attendant.name, generatedAt));
        y = renderSectionTitle(doc, 'Avaliacao e Diagnostico por IA', y, drawBrainIcon, PDF_COLORS.accent);

        // Bloco do Score IA com cores dinâmicas baseadas no percentual
        const scoreValIA = Number(aiAnalysis.scoreIA) || 0;
        let scoreColor = PDF_COLORS.accent;
        let scoreBgColor = PDF_COLORS.accentLight;
        let scoreColorHex = '#4f46e5';

        if (scoreValIA >= 80) {
            scoreColor = PDF_COLORS.success;
            scoreBgColor = PDF_COLORS.successLight;
            scoreColorHex = '#059669';
        } else if (scoreValIA >= 50) {
            scoreColor = PDF_COLORS.warning;
            scoreBgColor = PDF_COLORS.warningLight;
            scoreColorHex = '#d97706';
        } else {
            scoreColor = PDF_COLORS.danger;
            scoreBgColor = PDF_COLORS.dangerLight;
            scoreColorHex = '#dc2626';
        }

        doc.setFillColor(...scoreBgColor);
        doc.roundedRect(MARGIN, y, CONTENT_W, 36, 2, 2, 'F');
        doc.setDrawColor(...scoreColor);
        doc.setLineWidth(0.4);
        doc.roundedRect(MARGIN, y, CONTENT_W, 36, 2, 2, 'S');

        try {
            if (typeof document === 'undefined') throw new Error('Canvas indisponivel fora do navegador');
            const donutImg = generateDonutChartImage(scoreValIA, scoreColorHex, '#e2e8f0');
            doc.addImage(donutImg, 'PNG', MARGIN + 4, y + 7, 22, 22);
        } catch (e) {
            if (typeof document !== 'undefined') console.error('Erro ao desenhar o grafico de rosca no PDF:', e);
            doc.setFillColor(...scoreColor);
            doc.circle(MARGIN + 15, y + 18, 11, 'F');
            doc.setFillColor(...PDF_COLORS.accentLight);
            doc.circle(MARGIN + 15, y + 18, 8, 'F');
        }
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...scoreColor);
        doc.text(`${aiAnalysis.scoreIA}%`, MARGIN + 15, y + 19.3, { align: 'center' });

        // Texto Resumo Executivo
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        doc.text('Resumo Executivo da IA:', MARGIN + 30, y + 7);

        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...PDF_COLORS.primaryDark);
        
        const summaryText = aiAnalysis.resumo || 'Nenhum resumo gerado pela inteligencia artificial.';
        const splitSummary = doc.splitTextToSize(sanitizeText(summaryText), CONTENT_W - 35);
        doc.text(splitSummary, MARGIN + 30, y + 12.5);

        y += 42;

        // Pontos Fortes e Fracos Lado a Lado
        const boxW = (CONTENT_W - 4) / 2;
        const boxH = 42;

        ({ y, pageNum } = ensureSpace(doc, y, boxH + 4, pageNum, attendant.name, generatedAt));

        // PONTOS FORTES
        doc.setFillColor(...PDF_COLORS.successLight);
        doc.roundedRect(MARGIN, y, boxW, boxH, 2, 2, 'F');
        doc.setDrawColor(...PDF_COLORS.success);
        doc.setLineWidth(0.4);
        doc.roundedRect(MARGIN, y, boxW, boxH, 2, 2, 'S');

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.success);
        doc.text('Destaques (Pontos Fortes)', MARGIN + 5, y + 7);

        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        let pfY = y + 14;
        const pfList = aiAnalysis.pontosFortes || [];
        pfList.slice(0, 4).forEach(item => {
            doc.setFillColor(...PDF_COLORS.success);
            doc.circle(MARGIN + 6, pfY - 1, 1, 'F'); // bolinha marcadora
            const splitItem = doc.splitTextToSize(sanitizeText(item), boxW - 12);
            doc.text(splitItem, MARGIN + 9, pfY);
            pfY += (splitItem.length * 4) + 1.5;
        });

        // PONTOS FRACOS
        doc.setFillColor(...PDF_COLORS.dangerLight);
        doc.roundedRect(MARGIN + boxW + 4, y, boxW, boxH, 2, 2, 'F');
        doc.setDrawColor(...PDF_COLORS.danger);
        doc.setLineWidth(0.4);
        doc.roundedRect(MARGIN + boxW + 4, y, boxW, boxH, 2, 2, 'S');

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...PDF_COLORS.danger);
        doc.text('Oportunidades de Desenvolvimento', MARGIN + boxW + 9, y + 7);

        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_COLORS.textPrimary);
        let pdY = y + 14;
        const pdList = aiAnalysis.pontosFracos || [];
        pdList.slice(0, 4).forEach(item => {
            doc.setFillColor(...PDF_COLORS.danger);
            doc.circle(MARGIN + boxW + 10, pdY - 1, 1, 'F');
            const splitItem = doc.splitTextToSize(sanitizeText(item), boxW - 12);
            doc.text(splitItem, MARGIN + boxW + 13, pdY);
            pdY += (splitItem.length * 4) + 1.5;
        });

        y += boxH + 6;

        // ── EXEMPLOS DE ATENDIMENTOS (BONS E RUINS) ─────────────────────────────
        if (aiAnalysis.exemplosAtendimentos && aiAnalysis.exemplosAtendimentos.length > 0) {
            ({ y, pageNum } = ensureSpace(doc, y, 30, pageNum, attendant.name, generatedAt));
            y = renderSectionTitle(doc, 'Exemplos de Atendimentos (do pior ao melhor)', y, drawClipboardIcon, PDF_COLORS.accent);
            ({ y, pageNum } = renderAttendanceExamples(doc, aiAnalysis.exemplosAtendimentos, y, pageNum, attendant.name, generatedAt));
            y += 2;
        }
    }

    // ── HISTÓRICO DE AVALIAÇÕES RECENTES (Forçar Página 2+) ───────────────────
    if (observations && observations.length > 0) {
        // Como as seções anteriores devem ficar completas na página 1,
        // forçamos o início do Histórico de Avaliações na página 2.
        if (pageNum === 1) {
            drawPageFooter(doc, generatedAt);
            pageNum++;
            doc.addPage();
            drawPageHeader(doc, pageNum, attendant.name);
            y = 20; // margem inicial abaixo do header na página 2
        } else {
            ({ y, pageNum } = ensureSpace(doc, y, 25, pageNum, attendant.name, generatedAt));
        }

        const obsCount = Math.min(observations.length, 15);
        y = renderSectionTitle(doc, `Historico de Avaliacoes Recentes (${obsCount} de ${observations.length})`, y, drawClipboardIcon, PDF_COLORS.primaryMid);

        const colWidths = [30, 20, 14, 116];
        const tableResult = renderObservationsTable(doc, observations, y, colWidths, attendant.name, generatedAt, pageNum);
        y = tableResult.y;
        pageNum = tableResult.pageNum;
    }

    // Adiciona o rodapé na última página antes de salvar
    drawPageFooter(doc, generatedAt);

    // O contexto é recebido para manter compatibilidade com a chamada do modal.
    void reportContext;
    const filename = `Relatorio_Qualidade_${attendant.name.replace(/\s+/g, '_')}.pdf`;
    return { doc, filename, pageCount: doc.internal.getNumberOfPages() };
}

export async function exportAttendantPDF(...args) {
    const result = await buildAttendantPDF(...args);
    result.doc.save(result.filename);
    return { filename: result.filename, pageCount: result.pageCount };
}
